import { Box, styled } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconAdd, IconTrash } from 'assets/img';
import { LwButton, LwFormInput, LwIconButton } from 'redesign';
import { useFormArray } from 'shared/hooks/use-form-array';
import { AddClaFormValues, AddClaStepFormValues } from './add-cla-dialog.types';
import { StepItem } from './step-item';

export type ScaleItemProps = {
  scaleIndex: number;
  onRemove: (scaleIndex: number) => void;
};

const DEFAULT_STEP: AddClaStepFormValues = { step: '', startDate: '', endDate: '' };

export const ScaleItem = ({ scaleIndex, onRemove }: ScaleItemProps) => {
  const { t } = useTranslation('partner-screen-specific');
  const { control } = useFormContext<AddClaFormValues>();
  const {
    fields: stepFields,
    appendElement: appendStep,
    deleteElement: removeStep,
  } = useFormArray<AddClaFormValues, `scales.${number}.steps`>(
    `scales.${scaleIndex}.steps`,
    DEFAULT_STEP
  );

  return (
    <Wrapper>
      <ScaleRow>
        <ScaleField>
          <LwFormInput
            name={`scales.${scaleIndex}.scale`}
            control={control}
            label={t('partner-worker-detail.cla-form.scale-label', {
              ns: 'partner-screen-specific',
            })}
            placeholder={t('partner-worker-detail.cla-form.scale.placeholder', {
              ns: 'partner-screen-specific',
            })}
            rules={{
              required: t('partner-worker-detail.cla-form.scale.required', {
                ns: 'partner-screen-specific',
              }),
            }}
          />
        </ScaleField>
        <LwIconButton
          onClick={() => onRemove(scaleIndex)}
          color="error"
          iconColorMode="stroke"
          aria-label={t('partner-worker-detail.cla-form.scale.remove.label', {
            ns: 'partner-screen-specific',
            number: scaleIndex + 1,
          })}
          size="medium"
        >
          <IconTrash width={18} height={18} />
        </LwIconButton>
      </ScaleRow>

      <StepsBlock>
        {stepFields.map((field, stepIndex) => (
          <StepItem
            key={field.id}
            scaleIndex={scaleIndex}
            stepIndex={stepIndex}
            onRemove={removeStep}
          />
        ))}
        <AddStepButtonContainer>
          <LwButton
            onClick={() => appendStep()}
            startIcon={<IconAdd width={16} height={16} />}
            color="tertiary"
            iconColorMode="stroke"
          >
            {t('partner-worker-detail.cla-form.step.add', { ns: 'partner-screen-specific' })}
          </LwButton>
        </AddStepButtonContainer>
      </StepsBlock>
    </Wrapper>
  );
};

const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ScaleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
}));

const ScaleField = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingBottom: theme.spacing(1),
}));

const StepsBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const AddStepButtonContainer = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
}));
